import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FiSearch } from 'react-icons/fi'
import {
  FULL_VIEW_BOX,
  MAP_WIDTH,
  OUTLINES,
  haversineKm,
  project,
  toPolygonPoints,
  zoomToPoints,
} from './mapGeometry'
import { AREA_NAMES, resolveLocation } from './sgLocations'
import './style.scss'

const PROMINENT_COUNT = 9
const SEARCH_DEBOUNCE_MS = 300
const MIN_KEYWORD_LENGTH = 2
const PIN_PATH =
  'M0,0 C-2.2,-6.4 -14.3,-19.9 -14.3,-29.4 A14.3,14.3 0 1 1 14.3,-29.4 C14.3,-19.9 2.2,-6.4 0,0 Z'
const PIN_HEAD_Y = -29.4
const PIN_HEIGHT = 43.7
const PIN_AVATAR_RADIUS = 11.7
const PIN_AVATAR_CLIP_ID = 'group-map-pin-avatar'
const OUTLINE_POINTS = OUTLINES.map(toPolygonPoints)

const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2)
const lerpViewBox = (from, to, t) => ({
  x: from.x + (to.x - from.x) * t,
  y: from.y + (to.y - from.y) * t,
  w: from.w + (to.w - from.w) * t,
  h: from.h + (to.h - from.h) * t,
})
const initialOf = (group) => group.name.charAt(0).toUpperCase()

const Avatar = ({ group }) => {
  if (group.photoUrl) return <img src={group.photoUrl} alt='' />
  return initialOf(group)
}

// getAction(group) -> { label, pending, onClick }: what the Join control of a group shows/does
// searchGroups(keyword) -> Promise<groups>: optional group-name search, used when the query
// isn't a postal code / neighbourhood
const GroupMap = ({ groups, title, getAction, searchGroups, showHostList = false }) => {
  const svgRef = useRef(null)
  const stageRef = useRef(null)
  const tooltipRef = useRef(null)
  const viewBoxRef = useRef(FULL_VIEW_BOX)
  const hideTimerRef = useRef(null)
  const [viewBox, setViewBox] = useState(FULL_VIEW_BOX)
  const [query, setQuery] = useState('')
  const [tooltip, setTooltip] = useState(null) // { group, key, left, top, visible }
  const [tappedKey, setTappedKey] = useState(null)
  const [hoveredCardKey, setHoveredCardKey] = useState(null)
  const [nameSearch, setNameSearch] = useState(null) // { keyword, groups } of the last name search

  const origin = useMemo(() => resolveLocation(query), [query])
  const keyword = origin ? '' : query.trim()

  useEffect(() => {
    if (!searchGroups || keyword.length < MIN_KEYWORD_LENGTH) {
      setNameSearch(null)
      return undefined
    }
    let isStale = false
    const timer = setTimeout(() => {
      searchGroups(keyword)
        .then((found) => !isStale && setNameSearch({ keyword, groups: found || [] }))
        .catch(() => !isStale && setNameSearch({ keyword, groups: [] }))
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      isStale = true
      clearTimeout(timer)
    }
  }, [keyword])

  // A name search shows only the matching groups (which may be outside the default list)
  const shownGroups = nameSearch ? nameSearch.groups : groups

  const points = useMemo(
    // A group appears once per host, so the group id alone isn't unique
    () =>
      shownGroups.map((group, index) => ({
        group,
        key: `${group.id}-${index}`,
        ...project(group.lat, group.lng),
      })),
    [shownGroups]
  )

  // Idle / name search: the first few groups are featured.
  // Location search: the closest ones (with distance in km).
  const prominent = useMemo(() => {
    if (!origin) return points.slice(0, PROMINENT_COUNT).map((point) => ({ ...point, km: null }))
    const [lat, lng] = origin
    return points
      .map((point) => ({ ...point, km: haversineKm(lat, lng, point.group.lat, point.group.lng) }))
      .sort((a, b) => a.km - b.km)
      .slice(0, PROMINENT_COUNT)
  }, [points, origin])

  const prominentKeys = useMemo(() => new Set(prominent.map(({ key }) => key)), [prominent])

  const hideTooltip = () => {
    clearTimeout(hideTimerRef.current)
    setTooltip((prev) => prev && { ...prev, visible: false })
  }

  const scheduleHideTooltip = () => {
    clearTimeout(hideTimerRef.current)
    hideTimerRef.current = setTimeout(hideTooltip, 120)
  }

  const cancelHideTooltip = () => clearTimeout(hideTimerRef.current)

  const showTooltip = (point) => {
    cancelHideTooltip()
    const svgRect = svgRef.current.getBoundingClientRect()
    const stageRect = stageRef.current.getBoundingClientRect()
    const vb = viewBoxRef.current
    const pinTopY = point.y - PIN_HEIGHT * (vb.w / MAP_WIDTH)
    setTooltip({
      group: point.group,
      key: point.key,
      left: svgRect.left - stageRect.left + (point.x - vb.x) * (svgRect.width / vb.w),
      top: svgRect.top - stageRect.top + (pinTopY - vb.y) * (svgRect.height / vb.h) - 10,
      visible: true,
    })
  }

  const runAction = (group) => {
    const action = getAction(group)
    if (!action.pending) action.onClick()
  }

  // Touch has no hover: first tap shows the tooltip, a second tap on the same pin runs its action
  const handlePinClick = (point) => {
    if (tappedKey === point.key) {
      runAction(point.group)
      return
    }
    setTappedKey(point.key)
    showTooltip(point)
  }

  // Zoom into the searched area + its closest groups, onto the groups matching a name search,
  // or back out to the whole island
  useEffect(() => {
    hideTooltip()
    setTappedKey(null)

    const from = viewBoxRef.current
    let to = FULL_VIEW_BOX
    if (origin && prominent.length) {
      to = zoomToPoints([...prominent, project(origin[0], origin[1])], MAP_WIDTH * 0.58)
    } else if (nameSearch && prominent.length) {
      to = zoomToPoints(prominent, MAP_WIDTH * 0.58)
    }
    const duration = to === FULL_VIEW_BOX ? 450 : 500
    let startTime = null
    let frame = null

    const step = (timestamp) => {
      if (startTime === null) startTime = timestamp
      const t = Math.min(1, (timestamp - startTime) / duration)
      const next = lerpViewBox(from, to, easeInOut(t))
      viewBoxRef.current = next
      setViewBox(next)
      if (t < 1) frame = requestAnimationFrame(step)
    }
    frame = requestAnimationFrame(step)

    return () => cancelAnimationFrame(frame)
  }, [origin, nameSearch, prominent])

  useEffect(() => {
    const handleOutsideClick = ({ target }) => {
      if (svgRef.current?.contains(target) || tooltipRef.current?.contains(target)) return
      setTappedKey(null)
      hideTooltip()
    }
    document.addEventListener('click', handleOutsideClick)

    return () => {
      document.removeEventListener('click', handleOutsideClick)
      clearTimeout(hideTimerRef.current)
    }
  }, [])

  const tooltipKey = tooltip?.visible ? tooltip.key : null
  // A hovered card highlights its pin (without a tooltip); a hovered pin highlights its card
  const activeKey = tooltipKey || hoveredCardKey
  const pinScale = (viewBox.w / MAP_WIDTH).toFixed(4)
  // Draw the active pin last so it sits on top of its neighbours
  const orderedPins = activeKey
    ? [
        ...prominent.filter(({ key }) => key !== activeKey),
        ...prominent.filter(({ key }) => key === activeKey),
      ]
    : prominent
  const tooltipAction = tooltip && getAction(tooltip.group)

  return (
    <div className='group-map' id='hosts'>
      <div className='group-map__head'>
        {title && <h2 className='group-map__title'>{title}</h2>}
        <div className='group-map__search'>
          <FiSearch className='group-map__search-icon' />
          <input
            type='text'
            className='group-map__search-input'
            list={query ? 'group-map-areas' : undefined}
            placeholder={
              searchGroups
                ? 'Postal code, neighbourhood or group name'
                : 'Postal code or neighbourhood'
            }
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <datalist id='group-map-areas'>
            {AREA_NAMES.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </div>
      </div>

      <div className='group-map__stage' ref={stageRef}>
        <svg
          ref={svgRef}
          viewBox={`${viewBox.x.toFixed(2)} ${viewBox.y.toFixed(2)} ${viewBox.w.toFixed(
            2
          )} ${viewBox.h.toFixed(2)}`}
          xmlns='http://www.w3.org/2000/svg'
        >
          <defs>
            {/* Shared by every pin: clips in each pin's own (translated) coordinate space */}
            <clipPath id={PIN_AVATAR_CLIP_ID}>
              <circle cx='0' cy={PIN_HEAD_Y} r={PIN_AVATAR_RADIUS} />
            </clipPath>
          </defs>

          {OUTLINE_POINTS.map((outline) => (
            <polygon key={outline.slice(0, 20)} points={outline} className='group-map__outline' />
          ))}

          {points
            .filter(({ key }) => !prominentKeys.has(key))
            .map(({ key, x, y }) => (
              <circle
                key={key}
                cx={x.toFixed(1)}
                cy={y.toFixed(1)}
                r='1.9'
                className='group-map__dot'
              />
            ))}

          {orderedPins.map((point) => (
            <g
              key={point.key}
              className='group-map__pin'
              transform={`translate(${point.x.toFixed(1)},${point.y.toFixed(
                1
              )}) scale(${pinScale})`}
              onMouseEnter={() => showTooltip(point)}
              onMouseLeave={scheduleHideTooltip}
              onClick={() => handlePinClick(point)}
            >
              <g className={`group-map__pin-body${activeKey === point.key ? ' is-active' : ''}`}>
                <path d={PIN_PATH} className='group-map__pin-path' />
                <circle
                  cx='0'
                  cy={PIN_HEAD_Y}
                  r={PIN_AVATAR_RADIUS}
                  className='group-map__pin-dot'
                />
                {point.group.photoUrl ? (
                  <image
                    href={point.group.photoUrl}
                    x={-PIN_AVATAR_RADIUS}
                    y={PIN_HEAD_Y - PIN_AVATAR_RADIUS}
                    width={PIN_AVATAR_RADIUS * 2}
                    height={PIN_AVATAR_RADIUS * 2}
                    preserveAspectRatio='xMidYMid slice'
                    clipPath={`url(#${PIN_AVATAR_CLIP_ID})`}
                    className='group-map__pin-avatar'
                  />
                ) : (
                  <text
                    x='0'
                    y={PIN_HEAD_Y}
                    textAnchor='middle'
                    dominantBaseline='central'
                    className='group-map__pin-avatar'
                  >
                    {initialOf(point.group)}
                  </text>
                )}
              </g>
            </g>
          ))}
        </svg>

        <div
          ref={tooltipRef}
          className={`group-map__tooltip${tooltip?.visible ? ' is-visible' : ''}`}
          style={tooltip ? { left: tooltip.left, top: tooltip.top } : undefined}
          onMouseEnter={cancelHideTooltip}
          onMouseLeave={scheduleHideTooltip}
        >
          {tooltip && (
            <>
              <div className='group-map__tooltip-avatar'>
                <Avatar group={tooltip.group} />
              </div>
              <div className='group-map__tooltip-info'>
                <div className='group-map__tooltip-name'>{tooltip.group.name}</div>
                {tooltip.group.hostName && (
                  <div className='group-map__tooltip-host'>Hosted by {tooltip.group.hostName}</div>
                )}
              </div>
              <button
                type='button'
                className={`group-map__tooltip-join${tooltipAction.pending ? ' is-pending' : ''}`}
                disabled={tooltipAction.pending}
                onClick={() => runAction(tooltip.group)}
              >
                {tooltipAction.label}
              </button>
            </>
          )}
        </div>
      </div>

      {nameSearch && nameSearch.groups.length === 0 && (
        <p className='group-map__empty'>No groups match &ldquo;{nameSearch.keyword}&rdquo;.</p>
      )}

      {showHostList && prominent.length > 0 && (
        <div className='group-map__list-wrap'>
          <div className='group-map__list-title'>
            {origin && 'Closest hosts near you'}
            {!origin && nameSearch && `Groups matching “${nameSearch.keyword}”`}
            {!origin && !nameSearch && 'Our partner group buys'}
          </div>
          <div className='group-map__list'>
            {prominent.map((point, index) => {
              const action = getAction(point.group)
              const hosted = point.group.hostName ? `Hosted by ${point.group.hostName}` : ''
              const distance = point.km === null ? '' : `${point.km.toFixed(1)} km`
              return (
                <div
                  key={point.key}
                  className={`group-map__card${tooltipKey === point.key ? ' is-highlighted' : ''}`}
                  style={{ animationDelay: `${index * 40}ms` }}
                  onMouseEnter={() => {
                    hideTooltip()
                    setHoveredCardKey(point.key)
                  }}
                  onMouseLeave={() => setHoveredCardKey(null)}
                >
                  <div className='group-map__card-avatar'>
                    <Avatar group={point.group} />
                  </div>
                  <div className='group-map__card-info'>
                    <div className='group-map__card-name'>{point.group.name}</div>
                    <div className='group-map__card-sub'>
                      {[hosted, distance].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <button
                    type='button'
                    className={`group-map__card-join${action.pending ? ' is-pending' : ''}`}
                    disabled={action.pending}
                    onClick={(event) => {
                      event.stopPropagation()
                      runAction(point.group)
                    }}
                  >
                    {action.label}
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default GroupMap
